"use client";

import { useEffect, useState } from 'react';
import { useAuth, useUser } from '@clerk/nextjs';
import Link from 'next/link';

const ADMIN_ID = 'user_3AtnbF82V4ROWYZ4rH2PlrSsvGk';

type Ticket = {
  id: string;
  sin_description: string;
  additional_info: string | null;
  created_at: string;
};

export default function ConfessionListPage() {
  const { userId } = useAuth();
  const { isSignedIn } = useUser();
  const [tickets, setTickets] = useState<Ticket[]>([]);
  const [form, setForm] = useState({ sin_description: '', additional_info: '' });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const isAdmin = userId === ADMIN_ID;

  useEffect(() => {
    fetchTickets();
  }, [userId]);

  async function fetchTickets() {
    setLoading(true);
    setError(null);
    try {
      const response = await fetch('/api/church/confessions');
      if (!response.ok) {
        const payload = await response.json();
        setError(payload?.error || 'Unable to load confessions.');
        setTickets([]);
      } else {
        const data = await response.json();
        setTickets(data.tickets || []);
      }
    } catch (err) {
      setError('Unable to load confessions.');
      console.error(err);
    } finally {
      setLoading(false);
    }
  }

  async function handleSubmit(event: React.FormEvent<HTMLFormElement>) {
    event.preventDefault();
    if (!userId) {
      setError('Sign in before creating a confession ticket.');
      return;
    }

    if (!form.sin_description.trim()) {
      setError('You must describe the sin before submitting.');
      return;
    }

    setSaving(true);
    setError(null);

    try {
      const response = await fetch('/api/church/confessions', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          sin_description: form.sin_description,
          additional_info: form.additional_info,
        }),
      });

      const data = await response.json();
      if (!response.ok) {
        setError(data?.error || 'Failed to create ticket.');
      } else {
        setForm({ sin_description: '', additional_info: '' });
        fetchTickets();
      }
    } catch (err) {
      setError('Failed to create ticket.');
      console.error(err);
    } finally {
      setSaving(false);
    }
  }

  return (
    <main className="min-h-screen bg-stone-50 text-stone-900 font-serif px-6 py-10">
      <div className="max-w-4xl mx-auto space-y-8">
        <div className="rounded-3xl border border-stone-200 bg-white p-8 shadow-sm">
          <h1 className="text-3xl font-semibold">Church — Penance</h1>
          <p className="mt-3 text-stone-600">
            Make a confession for your sins and do penance. Only you and the Church admin can view your confession. Penance is given. It is anonymous, and is deleted after completion.
          </p>
         
        </div>

        <div className="grid gap-6 lg:grid-cols-[1.5fr_1fr]">
          <div className="rounded-3xl border border-stone-200 bg-white p-8 shadow-sm">
            <h2 className="text-2xl font-semibold">Make a new confession</h2>
            {!isSignedIn ? (
              <div className="mt-4 rounded-2xl bg-stone-50 p-4 text-stone-700">
                Sign in to make your anonymous confession.
              </div>
            ) : (
              <form className="mt-6 space-y-4" onSubmit={handleSubmit}>
                <div>
                  <label className="mb-2 block text-sm font-medium text-stone-700">Your sin</label>
                  <textarea
                    rows={5}
                    value={form.sin_description}
                    onChange={(event) => setForm(prev => ({ ...prev, sin_description: event.target.value }))}
                    className="w-full rounded-3xl border border-stone-200 bg-stone-50 p-4 text-sm text-stone-900 outline-none focus:border-amber-500 focus:ring-2 focus:ring-amber-100"
                    placeholder="Describe the sin you wish to confess"
                  />
                </div>
                <div>
                  <label className="mb-2 block text-sm font-medium text-stone-700">State of mind and circumstances</label>
                  <textarea
                    rows={4}
                    value={form.additional_info}
                    onChange={(event) => setForm(prev => ({ ...prev, additional_info: event.target.value }))}
                    className="w-full rounded-3xl border border-stone-200 bg-stone-50 p-4 text-sm text-stone-900 outline-none focus:border-amber-500 focus:ring-2 focus:ring-amber-100"
                    placeholder="Add any additional context, emotions, or circumstances"
                  />
                </div>
                {error && (
                  <div className="rounded-2xl bg-rose-50 p-3 text-sm text-rose-700">{error}</div>
                )}
                <button
                  type="submit"
                  disabled={saving}
                  className="inline-flex items-center justify-center rounded-full bg-amber-600 px-6 py-3 text-sm font-semibold text-white transition hover:bg-amber-700 disabled:cursor-not-allowed disabled:bg-stone-300"
                >
                  {saving ? 'Confessing…' : 'Make confession'}
                </button>
              </form>
            )}
          </div>

          <div className="rounded-3xl border border-stone-200 bg-white p-8 shadow-sm">
            <h2 className="text-2xl font-semibold">Your access</h2>
            <p className="mt-3 text-stone-600">
              {isAdmin
                ? 'You are the Church admin. You can view and close any confessions.'
                : 'Only you can view and respond to your anonymous confessions.'}
            </p>
            <div className="mt-6 rounded-3xl border border-stone-200 bg-stone-50 p-5">
              <p className="text-sm text-stone-500">Role</p>
              <p className="mt-2 text-lg font-medium text-stone-900">{isAdmin ? 'Church admin' : 'Penitent'}</p>
            </div>
          </div>
        </div>

        <div className="rounded-3xl border border-stone-200 bg-white p-8 shadow-sm">
          <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
            <div>
              <h2 className="text-2xl font-semibold">Confessions</h2>
              <p className="mt-2 text-sm text-stone-600">
                {isAdmin
                  ? 'All open tickets in the Church.'
                  : 'Your active confessions.'}
              </p>
            </div>
          </div>

          {loading ? (
            <div className="mt-6 text-stone-500">Loading confessions</div>
          ) : tickets.length === 0 ? (
            <div className="mt-6 rounded-3xl border border-dashed border-stone-200 bg-stone-50 p-6 text-stone-500">
              No confessions yet.
            </div>
          ) : (
            <div className="mt-6 space-y-4">
              {tickets.map((ticket) => (
                <Link
                  key={ticket.id}
                  href={`/church/penance/${ticket.id}`}
                  className="block rounded-3xl border border-stone-200 bg-stone-50 p-5 transition hover:border-amber-300 hover:bg-amber-50"
                >
                  <div className="flex items-center justify-between gap-4">
                    <div>
                      <p className="text-sm uppercase tracking-[0.2em] text-stone-400">Confession ticket</p>
                      <p className="mt-2 text-lg font-semibold text-stone-900">Ticket {ticket.id.slice(0, 8)}</p>
                    </div>
                    <p className="text-sm text-stone-500">{new Date(ticket.created_at).toLocaleString()}</p>
                  </div>
                  <p className="mt-4 text-sm leading-6 text-stone-700 line-clamp-3">{ticket.sin_description}</p>
                </Link>
              ))}
            </div>
          )}
        </div>
      </div>
    </main>
  );
}
